// useLiveSession — the live-call lifecycle (LIVE-VOICE-BUILD.md steps 4/5).
//
//   start() → POST /v1/realtime/session (Oriora auth — same Bearer as brainCall)
//          → WS to the vendor's Constrained endpoint with the SERVER-MINTED ticket
//            (the ticket carries the locked model+prompt+transcription config;
//             this client cannot override what the model is told)
//          → native plugin streams mic PCM16@16k frames in; model PCM16@24k out
//          → barge-in: vendor `interrupted` event → flushPlayback()
//          → transcripts of BOTH sides stream into `lines`
//
// ERROR CONTRACT (Bogdan 2026-08-12: "what happens if it throws inside the app"):
// every failure — session mint (401/402/403/503), socket error, socket close,
// plugin start failure, mid-call throw — lands in ONE place: status:'error' +
// a human `error` string, the call is torn down cleanly (mic OFF, playback
// stopped, socket closed), and the transcript SURVIVES on screen. No throw
// escapes to React; nothing keeps recording after a failure.
import { useCallback, useEffect, useRef, useState } from "react";
import { webStart, webPlay, webFlush, webStop } from "./webAudio";
import { invoke, Channel } from "@tauri-apps/api/core";
import { platform } from "../tauri-web";
import { getAccessToken } from "../supabase";
import { runVoiceTool } from "../chat/voiceTools";
import { CHAT_ACTIONS } from "../chat/brain";

const SERVER = "https://api.orioralabs.com";

export type LiveStatus = "idle" | "connecting" | "live" | "ended" | "error";

export interface TranscriptLine {
  role: "you" | "coach";
  text: string;
  final: boolean;
}

interface MicFrame { data: string; rms: number }

interface SessionTicket {
  vendor: string;
  model_id: string;
  token: string;
  expires_at: string;
  ws_url: string;
  native_model: string;
}

// ── native plugin wrappers (iOS; desktop errors loudly by design) ─────────────
// Returns simulator:true when the native mic path is absent (the sim's
// AURemoteIO input unit aborts the process — crash-proven 2026-08-12). The
// caller then streams the bundled test utterance as the "mic" so the FULL loop
// (session → socket → model hears speech → replies → playback → transcript →
// billing) stays sim-testable. Real capture is device-only, by construction.
//
// WHICH AUDIO PATH: iOS uses the native plugin (Apple's voice processing — the webview's AEC there
// was unreliable, which is why the plugin exists). macOS and web use the browser's own stack, the
// same one ChatGPT/Claude/Gemini use on desktop: getUserMedia gives us the platform echo canceller
// with nothing to build or ship. One call screen, two engines under it.
const useNativeAudio = () => platform() === "ios";

async function pluginStart(onFrame: (f: MicFrame) => void): Promise<{ simulator: boolean; hwRate?: number; micPerm?: string }> {
  if (!useNativeAudio()) {
    const { hwRate, micPerm } = await webStart(onFrame);
    return { simulator: false, hwRate, micPerm };
  }
  const ch = new Channel<MicFrame>();
  ch.onmessage = onFrame;
  const r = await invoke<{ simulator?: boolean; hwRate?: number; micPerm?: string } | null>("plugin:oriora-voice|start_call", { onFrame: ch });
  return { simulator: !!(r && r.simulator), hwRate: r?.hwRate, micPerm: r?.micPerm };
}

// Sim-mode "mic": stream the bundled 5s utterance in 100ms chunks, then let
// the vendor's VAD close the turn. Uses the SAME realtimeInput path as frames.
async function streamTestUtterance(
  send: (b64: string) => void,
  sendRaw: (msg: object) => void,
): Promise<void> {
  const buf = new Uint8Array(await (await fetch("/test-utterance.pcm")).arrayBuffer());
  const CHUNK = 3200; // 100ms @ 16k PCM16
  for (let off = 0; off < buf.length; off += CHUNK) {
    const slice = buf.subarray(off, off + CHUNK);
    let bin = "";
    for (let i = 0; i < slice.length; i++) bin += String.fromCharCode(slice[i]);
    send(btoa(bin));
    await new Promise((r) => setTimeout(r, 100));
  }
  // A real mic keeps streaming silence, which is how the vendor's VAD hears the
  // turn END. A canned clip just stops — without this signal the model waits
  // forever and never answers (sim-caught 2026-08-12: 50 frames out, 0 back).
  sendRaw({ realtimeInput: { audioStreamEnd: true } });
}
const pluginPlay  = (data: string) =>
  useNativeAudio() ? invoke("plugin:oriora-voice|play_audio", { payload: { data } }) : (webPlay(data), Promise.resolve());
const pluginFlush = () =>
  useNativeAudio() ? invoke("plugin:oriora-voice|flush_playback") : (webFlush(), Promise.resolve());
const pluginStop  = () =>
  useNativeAudio() ? invoke("plugin:oriora-voice|stop_call") : (webStop(), Promise.resolve());

// Device-side diagnostics (2026-08-12 — field failures were invisible through
// TestFlight rounds). Every stage of the call increments this; it renders as a
// small line in the sheet AND posts to /v1/realtime/diag (t+10s, t+30s, end)
// so the operator can read the device's view in the server logs. No audio
// content leaves the device through this — counters and stage names only.
interface Diag {
  build: string; transport: string; phases: string[];
  framesSent: number; lastRms: number; maxRms: number;
  scEvents: number; inTx: number; outTx: number; audioParts: number;
  hwRate?: number; simulator?: boolean;
}
const freshDiag = (): Diag => ({
  build: "1.3.4", transport: "direct", phases: [],
  framesSent: 0, lastRms: 0, maxRms: 0, scEvents: 0, inTx: 0, outTx: 0, audioParts: 0,
});

export function useLiveSession(appLanguage = "English") {
  const [status, setStatus] = useState<LiveStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  const [model, setModel] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [diagLine, setDiagLine] = useState("");

  const wsRef = useRef<WebSocket | null>(null);
  // What the customer last SAID (their own transcribed words). Consent for a destructive
  // server tool is read from this, never from a model-written flag.
  const lastHeardRef = useRef("");
  // The live session this call belongs to — the server scopes server-side tools to it.
  const sessionIdRef = useRef("");
  const liveRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Generation counter: teardown/start bump it, so an in-flight dial (e.g. the
  // relay retry) from a PREVIOUS call attempt can detect it's stale and stop.
  const genRef = useRef(0);

  const diagRef = useRef<Diag>(freshDiag());
  const diagTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const mark = (p: string) => { diagRef.current.phases.push(p); };
  const renderDiag = () => {
    const d = diagRef.current;
    setDiagLine(
      `${d.transport} · mic ${d.framesSent}f rms ${d.maxRms.toFixed(2)} · ` +
      `ev ${d.scEvents} · you ${d.inTx} coach ${d.outTx} · audio ${d.audioParts} · ` +
      (d.phases[d.phases.length - 1] ?? ""),
    );
  };
  const postDiag = (label: string) => {
    // fire-and-forget; diagnostics must never break or delay the call
    void (async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        await fetch(`${SERVER}/v1/realtime/diag`, {
          method: "POST",
          headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
          body: JSON.stringify({ label, ...diagRef.current }),
        });
      } catch { /* offline — nothing to do */ }
    })();
  };

  const appendLine = (role: TranscriptLine["role"], text: string) => {
    if (!text) return;
    setLines((prev) => {
      const last = prev[prev.length - 1];
      // stream partials into the open line for that role; a turn boundary finalizes
      if (last && last.role === role && !last.final) {
        return [...prev.slice(0, -1), { ...last, text: last.text + text }];
      }
      return [...prev, { role, text, final: false }];
    });
  };
  const finalizeLines = () =>
    setLines((prev) => prev.map((l) => (l.final ? l : { ...l, final: true })));

  // ONE teardown for every path — success, error, unmount. Idempotent.
  const teardown = useCallback((why: "ended" | "error", message?: string) => {
    // No status guard here: it captured a STALE snapshot in start()'s closure and
    // silently swallowed the error path (caught in sim bring-up). Idempotent anyway.
    genRef.current += 1; // aborts any in-flight dial/relay-retry from this attempt
    liveRef.current = false;
    for (const t of diagTimersRef.current.splice(0)) clearTimeout(t);
    mark("end:" + why + (message ? ":" + message.slice(0, 60) : ""));
    renderDiag();
    postDiag("end");
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { wsRef.current?.close(); } catch { /* already closed */ }
    wsRef.current = null;
    void pluginStop().catch(() => { /* engine may not have started */ });
    finalizeLines();
    if (why === "error") { setError(message ?? "The call failed."); setStatus("error"); }
    else setStatus("ended");
  }, []);

  useEffect(() => () => teardown("ended"), []); // unmount safety — mic never outlives the screen

  // ONE dial attempt over a given transport. "direct" = device→vendor socket
  // (fastest, but the vendor geo-checks the DEVICE's IP — VPNs and blocked
  // territories get rejected, Bogdan's phone 2026-08-12). A direct attempt that
  // fails BEFORE going live automatically redials as "relay": device→Oriora→
  // vendor, so the vendor only ever sees the server's IP — the same shape the
  // ChatGPT app uses, works wherever orioralabs.com is reachable. The relay
  // pipes frames verbatim, so everything below is transport-agnostic.
  const dial = useCallback(async (transport: "direct" | "relay") => {
    const gen = genRef.current;
    diagRef.current.transport = transport;
    mark("mint:" + transport);
    try {
      // 1. Oriora session — auth + wallet + router + ticket, all server-side.
      const token = await getAccessToken();
      if (!token) throw new Error("Please sign in to Oriora first.");
      const res = await fetch(`${SERVER}/v1/realtime/session`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        // The app's OWN action catalogue rides along: the server turns it into the call's
        // in-call tools, so voice can do whatever the typed chat can do. Without this the
        // server falls back to the three legacy library tools.
        body: JSON.stringify({
          app_id: "solo_journal", language: appLanguage, transport,
          actions: CHAT_ACTIONS,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 402) throw new Error("Not enough balance in your Oriora account — top up on the website, then try again.");
        if (res.status === 401) throw new Error("Please sign in to Oriora first.");
        throw new Error(body.error ?? `Could not start the call (${res.status}).`);
      }
      const { session_id, ticket } = (await res.json()) as { session_id: string; ticket: SessionTicket };
      if (gen !== genRef.current) return; // call was hung up while we were minting
      setModel(ticket.model_id);

      // Per-turn billing: the vendor reports usageMetadata on this socket after
      // each model turn; every report is posted so each RESPONSE bills at the
      // catalogue rate (the server's delta logic makes duplicate posts free).
      const postUsage = (u: { promptTokenCount?: number; responseTokenCount?: number }) => {
        void (async () => {
          try {
            const t = await getAccessToken();
            if (!t) return;
            await fetch(`${SERVER}/v1/realtime/usage`, {
              method: "POST",
              headers: { "content-type": "application/json", authorization: `Bearer ${t}` },
              body: JSON.stringify({
                session_id,
                prompt_tokens: u.promptTokenCount ?? 0,
                response_tokens: u.responseTokenCount ?? 0,
              }),
            });
          } catch { /* offline — the next report catches up (counts are cumulative) */ }
        })();
      };

      // 2. Socket — direct: vendor url + ephemeral ticket; relay: our server's
      // url + a single-use relay secret. No vendor key exists on this device.
      const ws = new WebSocket(`${ticket.ws_url}?access_token=${encodeURIComponent(ticket.token)}`);
      wsRef.current = ws;

      const failed = (msg: string) => teardown("error", msg);
      // A connect-phase failure on DIRECT falls back to RELAY instead of erroring
      // out — the user just sees "Calling…" continue. Relay failing = real error.
      const connectFailed = (msg: string) => {
        mark("connect-failed:" + msg.slice(0, 50));
        if (transport === "direct") {
          try { ws.close(); } catch { /* already closed */ }
          if (wsRef.current === ws) wsRef.current = null;
          if (gen === genRef.current) void dial("relay");
        } else failed(msg);
      };

      // The client setup carries the transcription flags too — the ticket locks
      // model+prompt, but the client-side setup message must not silently drop
      // the transcription config (both sides feed the on-screen transcript).
      ws.onopen = () => { mark("ws-open"); ws.send(JSON.stringify({ setup: {
        model: ticket.native_model,
        outputAudioTranscription: {},
        inputAudioTranscription: {},
      } })); };
      // ⚠️ These fire during CONNECTING too — a rejected/blocked socket must
      // surface, not hang "Calling…" forever (device-caught 2026-08-12: two
      // clean mints, then silence — the close event was being swallowed by a
      // liveRef guard until the call was live).
      let settled = false;
      const connectTimeout = setTimeout(() => {
        if (!liveRef.current && !settled) { settled = true;
          connectFailed("Couldn't reach the speech service (timed out). Check your connection and try again."); }
      }, 15000);
      ws.onerror = () => { if (!settled) { settled = true; clearTimeout(connectTimeout);
        connectFailed("Connection to the speech service failed."); } };
      ws.onclose = (e) => {
        clearTimeout(connectTimeout);
        const msg = e.reason ? "Disconnected: " + e.reason : "The call was disconnected (code " + e.code + ").";
        // Live call dropping = surface it (settled is true by then — it only
        // resolves the CONNECT phase, it must not swallow live disconnects).
        if (liveRef.current) { teardown(e.code === 1000 ? "ended" : "error", msg); return; }
        if (settled) return; // connect phase already resolved (error/timeout/fallback)
        settled = true;
        connectFailed(msg);
      };
      ws.onmessage = async (m) => {
        let j: any;
        try { j = JSON.parse(typeof m.data === "string" ? m.data : await (m.data as Blob).text()); }
        catch { return; }

        if (j.setupComplete) {
          mark("setup-complete");
          // 3. Socket ready → open the native audio engine.
          try {
            const sendAudio = (b64: string) => {
              const w = wsRef.current;
              if (w?.readyState === WebSocket.OPEN) {
                diagRef.current.framesSent += 1;
                w.send(JSON.stringify({ realtimeInput: { audio: {
                  mimeType: "audio/pcm;rate=16000", data: b64 } } }));
              }
            };
            const { simulator, hwRate, micPerm } = await pluginStart((f) => {
              diagRef.current.lastRms = f.rms;
              if (f.rms > diagRef.current.maxRms) diagRef.current.maxRms = f.rms;
              sendAudio(f.data);
            });
            diagRef.current.simulator = simulator;
            diagRef.current.hwRate = hwRate;
            mark("live:hw" + (hwRate ?? "?") + (micPerm ? ":" + micPerm : ""));
            liveRef.current = true;
            settled = true;
            clearTimeout(connectTimeout);
            setStatus("live");
            timerRef.current = setInterval(() => { setSeconds((s) => s + 1); renderDiag(); }, 1000);
            if (simulator) void streamTestUtterance(sendAudio, (msg) => {
              const w = wsRef.current;
              if (w?.readyState === WebSocket.OPEN) w.send(JSON.stringify(msg));
            });
          } catch (e) {
            mark("plugin-failed");
            failed(e instanceof Error ? e.message : "Microphone could not start.");
          }
          return;
        }

        if (j.usageMetadata) postUsage(j.usageMetadata);

        // ── in-call TOOLS: the model asks, the DEVICE does it, the answer goes straight
        // back on the socket (the data is on-device — nothing about it reaches Oriora).
        //
        // 2026-08-13: this used to hardcode three library verbs, which is why a call could
        // save a note but never touch the app's own entries. It now routes into
        // chat/voiceTools.ts → the SAME executor the typed chat uses, so voice does
        // whatever the app can do. Destructive verbs are re-checked THERE — a spoken "yes"
        // heard by the model is not a security boundary.
        if (j.toolCall?.functionCalls?.length) {
          const calls = j.toolCall.functionCalls as Array<{ id?: string; name: string; args?: Record<string, unknown> }>;
          const responses = await Promise.all(calls.map(async (fc) => {
            const r = await runVoiceTool(fc.name, fc.args ?? {}, lastHeardRef.current, sessionIdRef.current);
            // Show the REAL outcome in the transcript, never the model's claim — the same
            // rule the chat follows (APP-CHAT-AGENT-STANDARD §2.3).
            if (r.changed || !r.ok) appendLine("coach", `\n[${r.message}]\n`);
            return { id: fc.id, name: fc.name, response: { ok: r.ok, message: r.message } };
          }));
          const w = wsRef.current;
          if (w?.readyState === WebSocket.OPEN) {
            w.send(JSON.stringify({ toolResponse: { functionResponses: responses } }));
          }
          diagRef.current.phases.push("tool:" + calls.map((f) => f.name).join(","));
          return;
        }

        const sc = j.serverContent;
        if (!sc) return;
        diagRef.current.scEvents += 1;
        // barge-in: the model was interrupted by the user — silence it NOW.
        if (sc.interrupted) { void pluginFlush().catch(() => {}); finalizeLines(); }
        if (sc.inputTranscription?.text) { diagRef.current.inTx += sc.inputTranscription.text.length; appendLine("you", sc.inputTranscription.text);
          // Keep the customer's own recent words for the destructive-tool consent check.
          lastHeardRef.current = sc.inputTranscription.text.trim(); }
        if (sc.outputTranscription?.text) { diagRef.current.outTx += sc.outputTranscription.text.length; appendLine("coach", sc.outputTranscription.text); }
        for (const p of sc.modelTurn?.parts ?? []) {
          if (p.inlineData?.data) { diagRef.current.audioParts += 1; void pluginPlay(p.inlineData.data).catch(() => {}); }
        }
        if (sc.turnComplete) finalizeLines();
      };
    } catch (e) {
      // Session-mint failures (auth, balance, 503) would fail identically over
      // relay — no fallback for these, just the honest error.
      teardown("error", e instanceof Error ? e.message : "Could not start the call.");
    }
  }, [appLanguage, teardown]);

  const start = useCallback(async () => {
    genRef.current += 1;
    diagRef.current = freshDiag();
    diagTimersRef.current = [
      setTimeout(() => postDiag("t10"), 10_000),
      setTimeout(() => postDiag("t30"), 30_000),
    ];
    setError(null); setLines([]); setSeconds(0); setStatus("connecting");
    await dial("direct");
  }, [dial]);

  const stop = useCallback(() => teardown("ended"), [teardown]);

  return { status, error, lines, model, seconds, start, stop, diagLine };
}
